import React from 'react';
import { SvgIconProps } from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  RadioButtonUnchecked,
} from '@mui/icons-material';

/**
 * Health/logger status rendered as a *shape + colour*, never colour alone, so
 * the state is distinguishable for colourblind users (WCAG 1.4.1). Shapes are
 * deliberately distinct: tick / triangle / cross / open-circle.
 *
 * Colours match the existing dashboard status palette so this is a purely
 * additive change — same colours as before, now with a shape too.
 */
export type GlyphLevel = 'good' | 'warning' | 'error' | 'neutral';

const GLYPHS: Record<GlyphLevel, { Icon: React.ComponentType<SvgIconProps>; color: string; label: string }> = {
  good: { Icon: CheckCircle, color: '#4CAF50', label: 'Good' },
  warning: { Icon: Warning, color: '#FF9800', label: 'Warning' },
  error: { Icon: Cancel, color: '#f44336', label: 'Down' },
  neutral: { Icon: RadioButtonUnchecked, color: '#bdbdbd', label: 'N/A' },
};

/** Map a backend logger/location status code to a glyph level. */
export const loggerLevel = (status?: string): GlyphLevel =>
  status === 'good' ? 'good'
    : status === 'warning' ? 'warning'
    : status === 'error' ? 'error'
    : 'neutral';

interface StatusGlyphProps {
  level: GlyphLevel;
  /** Icon size in px. Defaults to 18 to match the dots it replaces. */
  size?: number;
  sx?: SvgIconProps['sx'];
}

export const StatusGlyph: React.FC<StatusGlyphProps> = ({ level, size = 18, sx }) => {
  const { Icon, color } = GLYPHS[level];
  return <Icon sx={{ fontSize: size, color, ...sx }} />;
};

/** Human label for a level — handy for tooltips/`aria-label`. */
export const glyphLabel = (level: GlyphLevel): string => GLYPHS[level].label;
